import {Image} from '@shopify/hydrogen';
import iconMagnifyingGlass from '../../assets/icons/icon-magnifying-glass.svg';
import {twMerge} from 'tailwind-merge';
import {IconClear} from './Icon';
import {useCallback, useState} from 'react';
import {DATA_TEST_IDS} from '~/lib/const';

export type SearchInputProps = {
  className?: string;
  name?: string;
  value: string;
  onChange: (next: string) => void;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  onEnter?: () => void;
  type?: string;
  placeholder?: string;
};

export function SearchAutocompleteInput({
  className = '',
  name = 'q',
  value,
  onChange,
  onFocus,
  onBlur,
  onEnter,
  type = 'text',
  placeholder = 'Search',
}: SearchInputProps) {
  const [isFocused, setIsFocused] = useState(false);

  const handleFocus = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      onFocus?.(event);
    },
    [onFocus],
  );

  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      onBlur?.(event);
    },
    [onBlur],
  );

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange(event.target.value);
    },
    [onChange],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        onEnter?.();
      }
    },
    [onEnter],
  );

  return (
    <div className="w-full bg-gray-200 rounded-[40px] relative h-[40px]">
      <input
        data-testid={DATA_TEST_IDS.INPUT('search')}
        type={type}
        name={name}
        autoComplete="off"
        placeholder={placeholder}
        className={twMerge(
          'bg-transparent py-0 text-black placeholder:text-gray-400 w-full focus:ring-0 border-0 transition focus:bg-gray/60 px-[50px] font-normal font-sans text-sm leading-[19px] h-[40px]',
          className,
        )}
        value={value}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        aria-label="Search"
        role="combobox"
        aria-controls="search-results"
        aria-expanded={isFocused}
        aria-autocomplete="list"
      />

      <button
        data-testid={DATA_TEST_IDS.BUTTON('search-submit')}
        type="submit"
        className="absolute left-0 bottom-0 top-0 py-2.5 pl-5 pr-2.5"
        aria-label="Submit search"
      >
        <Image
          loading="lazy"
          src={iconMagnifyingGlass}
          width="20px"
          height="20px"
          alt="search"
        />
      </button>

      {(isFocused || value.length > 0) && (
        <button
          data-testid={DATA_TEST_IDS.BUTTON('search-clear')}
          type="button"
          className="absolute right-7 top-2.5"
          onClick={() => onChange('')}
          aria-label="Clear search"
        >
          <IconClear />
        </button>
      )}
    </div>
  );
}
